import { filter } from "rxjs/operators";
/* Angular Component */
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  NgZone,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  Injector,
  OnInit,
} from "@angular/core";
/* Services */
import { UI } from "../../services/ui.service";
import { NavigationEnd, Router } from "@angular/router";
import { UserService } from "../../domain/user-service";
import { UploadService } from "../../infrastructure/upload/upload-service";
import { IndicateStatus } from "../../domain/enums";
import { GraphicsRepository } from "../../domain/graphics-repository";
import { ChannelPreviewComponent } from "../channel/channel-preview/channel-preview.component";
import { WindowsNotificationService } from "../../services/windows-notification.service";
import { ToastrService } from "ngx-toastr";
import { ReportsService } from "../../services/reports.service";
import { UploadStatusReport } from "../uploads-report/uploads-report.component";
import { UserBrandingService } from "../../domain/user-branding-service";
import { BackofficeUserBrandingSettings } from "../../domain/user-branding";
import { User } from "../../domain/user";
import {
  AlertParagraphText,
  IAlertObjectInterface
} from '../common/alert/alert.component';

const ALERT = "alert";
const CONFIRM = "confirm";

const PAGE_TITLES = {
  "/scheduler/calendar": "[[HEADER]] | Calendar Manager",
  "/mam/editMedia/": "[[HEADER]] | Edit Media Info",
  "/scheduler/graphics/new/": "[[HEADER]] | Create New Graphic",
  "/scheduler/graphics/edit/": "[[HEADER]] | Edit Graphic",
  "/scheduler/graphics": "[[HEADER]] | Your Graphics",
  "/autopilot/new": "[[HEADER]] | Autopilot | Create New Autopilot",
  "/autopilot/edit/": "[[HEADER]] | Autopilot | Edit",
  "/autopilot": "[[HEADER]] | Autopilot",
  "/channels/new": "[[HEADER]] | Create New Channel",
  "/channels/edit/": "[[HEADER]] | Channel Settings",
  "/report": "[[HEADER]] | Report",
  "/error": "[[HEADER]] | Error",
};

@Component({
  selector: "evrideo",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss", "../../../../fonts/ichannel.fonts.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  /* Define the alert object */
  alert: IAlertObjectInterface;
  window = window as any;
  konami = false;
  showGapReport = false;
  showMissingSubtitlesReport = false;
  showMissingAudiosReport = false;
  showEmptyContractsReport = false;
  userBrandingSettings: BackofficeUserBrandingSettings;
  currentUser: User;
  systemWorkingIndicatorStatus = IndicateStatus.None;

  @ViewChild(ChannelPreviewComponent) channelPreview: ChannelPreviewComponent;

  /* Define Services */
  constructor(
    private zone: NgZone,
    private ui: UI,
    public userService: UserService,
    private router: Router,
    private vcr: ViewContainerRef,
    private uploadService: UploadService,
    private app: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private graphicsRepository: GraphicsRepository,
    private toastr: ToastrService,
    private windowsNotificationService: WindowsNotificationService,
    private reportsService: ReportsService,
    private _userBrandingService: UserBrandingService,
  ) {
    this.uploadService.linkedFileComplete.subscribe((audioComplete) => {
      toastr.info(`Upload complete`, null);
    });
    this.uploadService.linkedFileError.subscribe((audioError) => {
      toastr.error(`Error uploading file`, null);
    });

    this.uploadService.mediaComplete.subscribe((mediaComplete) => {
      toastr.info(`Media upload complete`);
    });

    this.uploadService.mediaError.subscribe((mediaError) => {
      toastr.error(`Error uploading media`);
    });

    this.uploadService.packshotComplete.subscribe((packshotComplete) => {
      toastr.info(`Upload complete`, null);
    });
    this.uploadService.packshotError.subscribe((packshotError) => {
      toastr.error(`Error uploading file`, null);
    });
    this.uploadService.graphicsComplete.subscribe((graphicsComplete) => {
      toastr.info("Upload complete");
    });
    this.uploadService.graphicsError.subscribe((graphicsError) => {
      toastr.error("Error uploading file: " + graphicsError);
    });
    this.uploadService.thumbnailComplete.subscribe((thumbnailComplete) => {
      toastr.info("Upload complete");
    });
    this.uploadService.thumbnailError.subscribe((thumbnailError) => {
      toastr.error("Error uploading file: " + thumbnailError);
    });
    this.uploadService.fontComplete.subscribe((fontComplete) => {
      toastr.info("Upload complete");
    });
    this.uploadService.fontError.subscribe((fontError) => {
      toastr.error("Error uploading file: " + fontError);
    });
    this.graphicsRepository.templateDeleted.subscribe(() => {
      toastr.info("Template deleted");
    });
    this.uploadService.sampleVideoComplete.subscribe((sampleVideoComplete) => {
      toastr.info("Upload complete");
    });
    this.uploadService.sampleVideoError.subscribe((sampleVideoError) => {
      toastr.error("Error uploading file: " + sampleVideoError);
    });

    this.initWindowsEventListeners();

    this.alert = this.resetAlertObject();

    this.initUIEventListeners();

    this.router.events.subscribe(this.onRouteChange.bind(this));

    /* Mark as Success */
    this.systemWorkingIndicatorStatus = IndicateStatus.Success;

    this.initDOMEventListeners();
  }

  /**
     * Handler for router changes

     * @return void
     */
  async onRouteChange(event) {
    if (event instanceof NavigationEnd) {
      /* Assign the default page title */
      let title = "";

      /* Search and assign title based on possible available titles */
      // tslint:disable-next-line:max-line-length
      for (const key in PAGE_TITLES) {
        if (
          new RegExp(key.replace(/\//g, "\\/")).test(event.urlAfterRedirects)
        ) {
          title = PAGE_TITLES[key];
        }
      }

      /* Fallback to default */
      if (title !== "") {
        // apply user branding prefix
        if (!this.userBrandingSettings) {
          this.userBrandingSettings =
            await this._userBrandingService.getUserBrandingSettings();
        }

        document.title = title.replace(
          "[[HEADER]]",
          this.userBrandingSettings.tabTitlePrefix,
        );
      }
    }

    this.ui.notify(this.ui.EVENTS.CloseTopMenu, {});
  }

  onKeypress(event: KeyboardEvent) {}

  /**
     * Initializing Confirm Dialog

     * @return void
     */
  onConfirmDialog(event) {
    const data = event.data;

    /* Setup Basic State */
    this.alert = {
      type: CONFIRM,
      title: data.title ? data.title : "Opps..!",
      message: data.message ? data.message : "Unknown Error Has Occurred",
      buttons: [],
      options: data.options,
      display: true,
      callback: data.callback,
      bottomTexts: data.bottomTexts,
    };

    /* In Case Client Provided button */
    if (data["buttons"]) {
      /* Walking Over the buttons and adding them */
      data["buttons"].forEach(
        function (button) {
          /* Pushing buttons */
          this.alert.buttons.push(button);
        }.bind(this),
      );
    } else {
      /* Adding Default */
      this.alert.buttons.push({
        title: data.buttonTitle ? data.buttonTitle : "OK",
        callback:
          data.callback && typeof data.callback === "function"
            ? data.callback
            : undefined,
      });
    }
  }

  /**
     * Initializing Alert Notification

     */
  onShowAlert(event) {
    const data = event.data;

    this.alert = {
      type: ALERT,
      title: data.title ? data.title : "Opps..!",
      message: data.message ? data.message : "Unknown Error Has Occurred",
      buttons: [
        {
          title: data.buttonTitle ? data.buttonTitle : "OK",
          callback:
            data.callback && typeof data.callback === "function"
              ? data.callback
              : undefined,
        },
      ],
      display: true,
      options: [],
      bottomTexts: [],
      callback: data.callback
    };
  }

  /**
   * Dispatched When Alert Dismissed
   * @return void
   */
  onAlertDismissed() {
    this.alert = this.resetAlertObject();
  }

  /**
   * Reset Alert Object for the Basic Structure (Hidden)
   */
  resetAlertObject(): IAlertObjectInterface {

    return  {
      title: '',
      message: '',
      callback: undefined,
      type: ALERT,
      display: false,
      options: null,
      buttons: [],
      bottomTexts: []
    };

  }

  /**
   * Booting the Component
   */
  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    if (!this.currentUser) {
      this.router.navigateByUrl("/error", {
        skipLocationChange: true,
        replaceUrl: true,
      });
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (
          (<NavigationEnd>event).url.indexOf("/channels") !== -1 &&
          this.channelPreview.isPreviewOpen()
        ) {
          this.channelPreview.close();
        }
      });
  }

  private initDOMEventListeners() {
    /* support menu shortcuts */
    document.addEventListener("keypress", this.onKeypress.bind(this), false);

    window.addEventListener("offline", function (event) {}.bind(this), false);

    this.window.kkeys = [];
    window.addEventListener(
      "keydown",
      (e) => {
        const length = this.window.kkeys.length;
        if (length > 100) {
          (window as any).kkeys = [];
          return;
        }
        this.window.kkeys.push(e.keyCode);
        if (
          this.window.kkeys[length - 1] === 38 &&
          length > 1 &&
          length !== 2 &&
          this.window.kkeys[length - 2] === 38
        ) {
          this.window.kkeys = this.window.kkeys.slice(length - 2);
        }

        if (
          this.window.kkeys
            .toString()
            .indexOf("38,38,40,40,37,39,37,39,66,65") >= 0
        ) {
          this.konami = true;
          let flickerInterval;
          const init = () => {
            document.body.style.overflow = "hidden";
            const canvas = document.getElementById("konami") as any;
            const ctx = canvas.getContext("2d");
            canvas.width = 700;
            canvas.height = 500;
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fill();
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pix = imgData.data;
            flickerInterval = setInterval(() => {
              for (let i = 0; i < pix.length; i += 4) {
                const color = Math.random() * 255 + 50;
                pix[i] = color;
                pix[i + 1] = color;
                pix[i + 2] = color;
              }
              ctx.putImageData(imgData, 0, 0);
            }, 30);
            setTimeout(() => {
              this.konami = false;
              this.window.kkeys = [];
              clearInterval(flickerInterval);
              document.body.style.overflow = "initial";
            }, 20000);
          };
          init();
          console.log(
            `big thanks to ademilter [https://codepen.io/ademilter/pen/hDtpq]`,
          );
        }
      },
      true,
    );
  }

  private initUIEventListeners() {
    /* Dismissing Alert or Dialog */
    this.ui.listen(
      this.ui.EVENTS.AlertDismissed,
      this.onAlertDismissed.bind(this),
    );

    /* Display Alert Dialog Listener */
    this.ui.listen(this.ui.EVENTS.ShowAlert, this.onShowAlert.bind(this));

    /* Display Confirm Dialog Listener */
    this.ui.listen(this.ui.EVENTS.ShowConfirm, this.onConfirmDialog.bind(this));
  }

  private initWindowsEventListeners() {
    this.windowsNotificationService.ACTIONS.showGapReport.subscribe(() => {
      this.showGapReport = true;
    });

    this.windowsNotificationService.ACTIONS.closeGapReport.subscribe(() => {
      this.showGapReport = false;
    });

    this.windowsNotificationService.ACTIONS.showMissingSubtitlesReport.subscribe(
      () => {
        this.showMissingSubtitlesReport = true;
      },
    );

    this.windowsNotificationService.ACTIONS.closeMissingSubtitlesReport.subscribe(
      () => {
        this.showMissingSubtitlesReport = false;
      },
    );

    this.windowsNotificationService.ACTIONS.closeMissingAudiosReport.subscribe(
      () => {
        this.showMissingAudiosReport = false;
      },
    );

    this.windowsNotificationService.ACTIONS.showMissingAudiosReport.subscribe(
      () => {
        this.showMissingAudiosReport = true;
      },
    );

    this.windowsNotificationService.ACTIONS.showEmptyContractsReport.subscribe(
      () => {
        this.showEmptyContractsReport = true;
      },
    );

    this.windowsNotificationService.ACTIONS.closeEmptyContractsReport.subscribe(
      () => {
        this.showEmptyContractsReport = false;
      },
    );

    this.reportsService.reportsGlobalState.subscribe((newState) => {
      if (
        newState.reportAlertsSettings &&
        newState.reportAlertsSettings.gapsReportEnabled === false
      ) {
        this.showGapReport = false;
      }

      if (
        newState.reportAlertsSettings &&
        newState.reportAlertsSettings.missingSubtitleReportEnabled === false
      ) {
        this.showMissingSubtitlesReport = false;
      }

      if (
        newState.reportAlertsSettings &&
        newState.reportAlertsSettings.missingAudiosReportEnabled === false
      ) {
        this.showMissingAudiosReport = false;
      }

      if (
        newState.reportAlertsSettings &&
        newState.reportAlertsSettings.emptyContractReportEnabled === false
      ) {
        this.showEmptyContractsReport = false;
      }
    });
  }
}
